import * as _ from 'lodash';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import type { VoidFunctionComponent } from 'react';
import type { Simplify } from 'type-fest';

import type { Category, AnyExportRequest, Issue } from '../../../../../../shared/issuesV2';
import { JIRA_CONTENT_MAX_LENGTH, COUNT_MAX_FE } from '../../../../../../shared/issuesV2';
import {
  FormGroup,
  Icon,
  icons,
  LabeledTextArea,
  LabeledTextInput,
  RouterLink,
} from '../../../../generic';
import ModalIssueExportFields from '../ModalIssueExportFields';
import useUniqueTicketEditor from '../useUniqueTicketEditor';
import OrganizationsModule from '../../../../../redux/organizations';
import type { Jira } from '../../../../../../shared/model-typings';
import { nuc } from '../../../../../utils';
import ProjectStore from '../../../../../stores/ProjectStore';
import useJiraExport from '../useJiraExport';
import useTrackerExportPicker from '../useTrackerExportPicker';
import type { Project, SetState } from '../../../../../types';
import { useOnMount } from '../../../../../hooks';
import { window } from '../../../../../globals';
import * as BuildTicket from '../buildTicketContent';
import { plural, pluralizeIssues } from '../../../../../../shared/helpers';
import Text from '../../../../generic/Text';
import UniqueJiraTicketEditor from './UniqueJiraTicketEditor';
import ExportIssueModalWarning from '../ExportIssueModalWarning';

const SetupJiraContent: VoidFunctionComponent = () => (
  <div>
    <Text.H3>JIRA Integration Required</Text.H3>
    <span className="issues-modal__body__message">
      <span>
        Your organization has not setup a JIRA integration yet.
        <RouterLink to="/account/settings/integrations">Configure JIRA <Icon icon={icons.externalLink} /></RouterLink>
      </span>
    </span>
  </div>
);

const JiraProjectSettingsFields = [
  'issue_tracker_type',
  'issue_tracker_url',
  'issueTrackerProjectIds',
  'issueTrackerIssueTypes',
  'issueTrackerLabels',
  'issueTrackerCustomFields',
  'issueTrackerComponents',
  'useGlobalTrackerSettings',
] as const;

export type JiraProjectSettings = Simplify<Pick<Project, typeof JiraProjectSettingsFields[number]> & {
  projectJiraSettingsURL: string;
}>;

//  Grabs the project from the project Reflux store and returns the Jira settings for that project, if they exist.
const getProjectJiraSettings = (projectId: string): JiraProjectSettings | { useGlobalTrackerSettings: true } => {
  const { data: project } = (ProjectStore as any)?.getProject(nuc(projectId)) ?? { data: null };
  //  For cases where this is being rendered on the global issues page,
  //  there is no "project settings" to pull from, so always just use the global tracker settings.
  if (!project) {
    return { useGlobalTrackerSettings: true };
  }

  return {
    ..._.pick(project, JiraProjectSettingsFields),
    projectJiraSettingsURL: `/projects/${nuc(project.locator as string)}/settings/issues`,
  };
};

type Props = {
  shouldDisableInputs?: boolean;
  issueCategory: Category;
  selectedIssuesCount: number;
  projectId?: string;
  isLoading: boolean;
  setWarningMessage: SetState<string>;
  issues: Issue[];
  setContent: SetState<AnyExportRequest>;
  jiraTicketCount?: number;
  setIsDisabled: SetState<boolean>;
};

const JiraTicketExportContent: VoidFunctionComponent<Props> = ({
  selectedIssuesCount,
  shouldDisableInputs,
  issueCategory,
  projectId,
  isLoading,
  setWarningMessage,
  issues,
  setContent,
  jiraTicketCount,
  setIsDisabled,
}) => {
  const jiras: Jira[] = useSelector(OrganizationsModule.selectors.getCurrentJiras) || [];
  const hasJiras = (jiras?.filter((jira) => jira.enabled)?.length ?? 0) > 0;
  const { trackerType, onTrackerTypeChange } = useTrackerExportPicker(hasJiras, false);
  const [title, setTitle] = useState('');
  const [content, setInternalContent] = useState('');

  const uniqueTicketEditor = useUniqueTicketEditor();

  const { exportOptions, required, optional } = useJiraExport({
    jiras,
    issueCategory,
    projectSettings: getProjectJiraSettings(projectId as string) as JiraProjectSettings,
  });

  // If any of the fields update, update the content state at the top level.
  useEffect(() => {
    setContent({
      type: 'jira',
      title,
      description: content,
      uniqueTickets: uniqueTicketEditor?.isEnabled ? uniqueTicketEditor.tickets : [],
      trackerId: exportOptions.project_id,
      jiraId: exportOptions.jira_id,
      issueType: exportOptions.issueType,
      labels: exportOptions.labels,
      customFields: exportOptions.customFields,
      components: exportOptions.components,
    });

    if (content.length > JIRA_CONTENT_MAX_LENGTH) {
      setIsDisabled(true);
    }
  }, [
    content,
    exportOptions,
    setContent,
    title,
    uniqueTicketEditor?.isEnabled,
    uniqueTicketEditor?.tickets,
    setIsDisabled,
  ]);

  const [currentJira] = jiras.filter(jira => jira.id === exportOptions.jira_id);
  useEffect(() => {
    uniqueTicketEditor?.setIsEnabled(currentJira?.defaultUniqueTickets || false);
  },
  // We only want this to run once for a list of issues, so we only depend on
  // the jira issues, not the uniqueTicketEditor.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [currentJira]);

  useOnMount(() => {
    // Disable the submit button if there are no configured Jiras.
    if (!hasJiras) {
      setIsDisabled(true);
    }

    // Init single ticket state
    setTitle(BuildTicket.Title(issues, selectedIssuesCount));
    setInternalContent(BuildTicket.Content(issues, issueCategory, selectedIssuesCount, window.location, window.user));

    // Init unique tickets state
    uniqueTicketEditor.setTickets(
      issues.map((issue) => ({
        issueId: issue.id,
        title: BuildTicket.Title([issue], 1),
        content: BuildTicket.Content([issue], issueCategory, 1, window.location, window.user),
      })),
    );

    const issuesWithTicketCount = issues.reduce(
      (accum: number, issue: Issue) => accum + (issue.jiraTickets?.length ?? 0),
      0,
    );

    // Show count only if they have less than max amount of issues allowed selected as we don't load *total* count if more than that
    if ((selectedIssuesCount <= COUNT_MAX_FE && issuesWithTicketCount > 0) || !!jiraTicketCount) {
      const countToUse = jiraTicketCount || issuesWithTicketCount;
      setWarningMessage(
        `${countToUse} selected FOSSA ${pluralizeIssues(countToUse)} will no longer be associated with ${plural(
          countToUse,
          'its',
          'their',
        )} previous Jira ticket.`,
      );
    } else {
      setWarningMessage('');
    }
  });

  // If there are no Jiras set up, display the message and link for configuring.
  if (!hasJiras) {
    return <SetupJiraContent />;
  }

  return (
    <>
      {content?.length > JIRA_CONTENT_MAX_LENGTH && (
        <ExportIssueModalWarning
          message={`JIRA ticket content cannot exceed ${JIRA_CONTENT_MAX_LENGTH} characters. Please edit the content to reduce its length.`}
        />
      )}
      <UniqueJiraTicketEditor uniqueTicketEditor={uniqueTicketEditor} selectedIssuesCount={selectedIssuesCount} />
      <div className="modal-issue-export--layout">
        <ModalIssueExportFields
          required={required}
          optional={optional}
          trackerType={trackerType}
          onTrackerTypeChange={onTrackerTypeChange}
          hasGitHub={false}
          hasJiras={hasJiras}
        />
        <section className="modal-issue-export__content">
          <FormGroup>
            <LabeledTextInput
              label="Title"
              value={uniqueTicketEditor?.isEnabled ? uniqueTicketEditor.title : title}
              isDisabled={isLoading || shouldDisableInputs}
              onChange={uniqueTicketEditor?.isEnabled ? uniqueTicketEditor.setTitle : setTitle}
            />
            <LabeledTextArea
              label="Content"
              value={uniqueTicketEditor?.isEnabled ? uniqueTicketEditor.content : content}
              isDisabled={isLoading || shouldDisableInputs}
              showCharCount
              maxLength={JIRA_CONTENT_MAX_LENGTH}
              onChange={uniqueTicketEditor?.isEnabled ? uniqueTicketEditor.setContent : setInternalContent}
            />
          </FormGroup>
        </section>
      </div>
    </>
  );
};

export default JiraTicketExportContent;
